import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { 
  LayoutDashboard, 
  CreditCard, 
  Users, 
  Pill, 
  LogOut,
  ShieldCheck,
  MessageSquare,
  Menu,
  X,
  Building2
} from 'lucide-react';
import { signOut } from '../lib/auth-client';

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = async () => {
    await signOut();
    window.location.href = '/login';
  };

  const closeSidebar = () => setIsOpen(false);

  // Close sidebar on route change (clicking a link)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setIsOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navItems = [
    { to: '/', end: true, icon: <LayoutDashboard size={20} />, label: 'Dashboard' },
    { to: '/payments', icon: <CreditCard size={20} />, label: 'Payments' },
    { to: '/pharmacies', icon: <Building2 size={20} />, label: 'Pharmacies' },
    { to: '/staff', icon: <Users size={20} />, label: 'Staff' },
    { to: '/medicines', icon: <Pill size={20} />, label: 'Medicines' },
    { to: '/contacts', icon: <MessageSquare size={20} />, label: 'Contacts' },
  ];

  return (
    <>
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200 flex items-center justify-between px-4 h-14 shadow-sm">
        <div className="flex items-center gap-2 text-primary-600">
          <ShieldCheck size={26} strokeWidth={2.5} />
          <span className="text-lg font-bold tracking-tight text-gray-900">Oshud Admin</span>
        </div>
        <button
          onClick={() => setIsOpen(true)}
          className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
          aria-label="Open menu"
        >
          <Menu size={22} />
        </button>
      </div>

      {/* Overlay backdrop (mobile) */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm animate-in fade-in duration-300"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar drawer */}
      <aside
        className={`
          fixed top-0 left-0 h-screen z-50 w-64 bg-white border-r border-gray-200
          flex flex-col p-6 transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 shadow-2xl lg:shadow-none
        `}
      >
        {/* Logo + close button (mobile) */}
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3 text-primary-600">
            <ShieldCheck size={32} strokeWidth={2.5} />
            <span className="text-xl font-bold tracking-tight text-gray-900">Oshud Admin</span>
          </div>
          <button
            onClick={closeSidebar}
            className="lg:hidden p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex flex-col gap-2">
          {navItems.map(({ to, end, icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={closeSidebar}
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            >
              {icon}
              <span className="font-medium">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 text-gray-600 rounded-lg hover:bg-red-50 hover:text-red-600 transition-all duration-200"
          >
            <LogOut size={20} />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
